import React from 'react';
import { AnchorStyle, PrimaryStyle } from './styles';
import formatPhoneNumber from '../../../../lib/utils/common/formatPhoneNumber';
import SendMessageService from '../../../../containers/app/common/SendMessageService';
import { AMONDZ_MEMBER_TYPE } from '../../../../constants/service/common/utilConstants';
import { SettlementItem } from '../../types/columns';
import { formatDate } from '../../../../lib/utils/date/formatDate';
import { DATE_FORMAT } from '../../../../types/common/date';
import TanstackTableFilter from '../../../../components/TanstackTable/TanstackTableFilter';
import { numberStringRegExp } from '../../../../regExp/numberStringRegExp';
import { FilterFn } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import { numberAndCommaWithSpaceRegExp } from '../../../../regExp/numberAndCommaWithSpaceRegExp';

const includesSome: FilterFn<SettlementItem> = (row, columnId, filterValue) => {
  const cellValue = String(row.getValue(columnId));

  const filterValues = filterValue
    .split(',')
    .map((v: string) => v.trim())
    .filter((v: string) => v !== '');

  return filterValues.includes(cellValue);
};

const columnHelper = createColumnHelper<SettlementItem>();

export const columns = [
  columnHelper.accessor('id', {
    id: 'id',
    header: '정산번호',
    size: 100,
    enableSorting: true,
    enableColumnFilter: true,
    cell: (cell) => <PrimaryStyle>{cell.getValue()}</PrimaryStyle>,
    filterFn: includesSome,
    meta: {
      Filter: ({ column }) => <TanstackTableFilter regexp={numberAndCommaWithSpaceRegExp} column={column} />,
    },
  }),
  columnHelper.accessor('brandName', {
    header: '브랜드',
    size: 100,
    enableSorting: false,
    enableColumnFilter: true,
  }),

  columnHelper.accessor('brandId', {
    id: 'brandId',
    header: '브랜드 ID',
    size: 80,
    enableSorting: false,
    enableColumnFilter: true,
    cell: (cell) => (
      <AnchorStyle target="_blank" href={`/store/detail/${cell.getValue()}`} rel="noreferrer noopener">
        {cell.getValue()}
      </AnchorStyle>
    ),
    filterFn: 'weakEquals',
    meta: {
      Filter: ({ column }) => <TanstackTableFilter regexp={numberStringRegExp} column={column} />,
    },
  }),
  columnHelper.accessor('orderId', {
    id: 'orderId',
    header: '주문 번호',
    size: 140,
    enableSorting: false,
    enableColumnFilter: true,
    filterFn: 'weakEquals',
    meta: {
      Filter: ({ column }) => <TanstackTableFilter regexp={numberStringRegExp} column={column} />,
    },
  }),
  columnHelper.accessor('orderItemId', {
    header: '상품 주문 번호',
    size: 140,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell): JSX.Element | string => {
      const orderItemId = cell.getValue();
      return orderItemId ? (
        <AnchorStyle target="_blank" href={`/order/detail/${orderItemId}`} rel="noreferrer noopener">
          {orderItemId}
        </AnchorStyle>
      ) : (
        '-'
      );
    },
  }),
  columnHelper.accessor('periodType', {
    header: '정산 주기 타입',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor('monthlyExecutionPeriod', {
    header: '월간 정산 주기',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => cell.getValue() || '-',
  }),
  columnHelper.accessor('monthlyExecutionDay', {
    header: '월간 정산일',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => cell.getValue() || '-',
  }),
  columnHelper.accessor('dueDate', {
    header: '정산 예정일',
    size: 120,
    enableSorting: true,
    enableColumnFilter: false,
    cell: (cell) => cell.getValue() || '-',
  }),
  columnHelper.accessor('baseDate', {
    header: '정산 기준일',
    size: 120,
    enableSorting: true,
    enableColumnFilter: false,
    cell: (cell) => cell.getValue() || '-',
  }),
  columnHelper.accessor('completeDate', {
    header: '정산 완료일',
    size: 120,
    enableSorting: true,
    enableColumnFilter: false,
    cell: (cell) => cell.getValue() || '-',
  }),
  columnHelper.accessor('status', {
    header: '상태',
    size: 80,
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor('type', {
    header: '구분',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor('productId', {
    header: '상품번호',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell): JSX.Element | string => {
      const productId = cell.getValue();

      return productId ? (
        <AnchorStyle target="_blank" href={`/product/detail/${productId}?tabKey=2`} rel="noreferrer noopener">
          {productId}
        </AnchorStyle>
      ) : (
        '-'
      );
    },
  }),
  columnHelper.accessor('pgProviderKorName', {
    header: 'PG사 정보',
    size: 120,
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor('paymentMethod', {
    header: '결제수단',
    size: 120,
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor('userName', {
    header: '구매자명',
    size: 180,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell): JSX.Element => {
      const userId = cell.row.original.userId;

      return (
        <AnchorStyle target="_blank" href={`/account/user/detail/${userId}`} rel="noreferrer noopener">
          {cell.getValue() || userId}
        </AnchorStyle>
      );
    },
  }),
  columnHelper.accessor('settlementAmount', {
    header: '정산금액',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('salesAmount', {
    header: '판매금액',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('usedCouponAmount', {
    header: '총 쿠폰 사용금액',
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => (typeof cell.getValue() === 'undefined' ? '-' : Number(cell.getValue()).toLocaleString()),
  }),
  columnHelper.accessor('amondzCouponAmount', {
    header: '쿠폰 분담액(amondz)',
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => (typeof cell.getValue() === 'undefined' ? '-' : Number(cell.getValue()).toLocaleString()),
  }),
  columnHelper.accessor('couponShareAmount', {
    header: '쿠폰 분담액(브랜드)',
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => (typeof cell.getValue() === 'undefined' ? '-' : Number(cell.getValue()).toLocaleString()),
  }),
  columnHelper.accessor('usedPointAmount', {
    header: '포인트',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => (typeof cell.getValue() === 'undefined' ? '-' : Number(cell.getValue()).toLocaleString()),
  }),
  columnHelper.accessor('paymentAmount', {
    header: '결제금액',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('paymentDate', {
    header: '결제일시',
    size: 140,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell): string => {
      const paymentDate = cell.getValue();

      return typeof paymentDate === 'string' && paymentDate
        ? formatDate(paymentDate, DATE_FORMAT.YMD_HM_DASH_FORMAT)
        : '-';
    },
  }),
  columnHelper.accessor('purchaseConfirmDate', {
    header: '구매 확정 일시',
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell): string => {
      const purchaseConfirmDate = cell.getValue();

      return typeof purchaseConfirmDate === 'string' && purchaseConfirmDate
        ? formatDate(purchaseConfirmDate, DATE_FORMAT.YMD_HM_DASH_FORMAT)
        : '-';
    },
  }),
  columnHelper.accessor('sumFee', {
    header: '수수료 합계',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('management', {
    header: '관리처',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => cell.getValue() || '-',
  }),
  columnHelper.accessor('productManagementType', {
    header: '타입',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => cell.getValue() || '-',
  }),
  columnHelper.accessor('pgFee', {
    header: 'PG 수수료',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('amondzFee', {
    header: 'AMONDZ 수수료',
    size: 120,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('marketingFee', {
    header: '마케팅 수수료',
    size: 120,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('fulfillmentFee', {
    header: '풀필먼트 수수료',
    size: 140,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('pgFeeVat', {
    header: 'PG 수수료 VAT',
    size: 140,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('amondzFeeVat', {
    header: 'AMONDZ 수수료 VAT',
    size: 150,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('marketingFeeVat', {
    header: '마케팅 수수료 VAT',
    size: 140,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('fulfillmentFeeVat', {
    header: '풀필먼트 수수료 VAT',
    size: 140,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => Number(cell.getValue()).toLocaleString(),
  }),
  columnHelper.accessor('accountHolder', {
    id: 'accountHolder',
    header: '예금주',
    size: 140,
    enableSorting: false,
    enableColumnFilter: true,
    cell: (cell) => cell.getValue() || '-',
  }),
  columnHelper.accessor('accountNumber', {
    id: 'accountNumber',
    header: '계좌번호',
    size: 140,
    enableSorting: false,
    enableColumnFilter: true,
    cell: (cell) => cell.getValue() || '-',
    filterFn: 'weakEquals',
    meta: {
      Filter: ({ column }) => <TanstackTableFilter regexp={numberStringRegExp} column={column} />,
    },
  }),
  columnHelper.accessor('bankName', {
    id: 'bankName',
    header: '은행',
    size: 100,
    enableSorting: false,
    enableColumnFilter: true,
    cell: (cell) => cell.getValue() || '-',
  }),
  columnHelper.accessor('brandPhone', {
    header: '판매자 연락처',
    size: 160,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => (
      <SendMessageService
        text={formatPhoneNumber(cell.getValue())}
        memberType={AMONDZ_MEMBER_TYPE.SELLER}
        targetId={cell.row.original.brandId}
      />
    ),
  }),
  columnHelper.accessor('adminId', {
    header: '관리자',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell): string => {
      const { adminName } = cell.row.original;

      return adminName ? `${adminName}(${cell.getValue()})` : '-';
    },
  }),
  columnHelper.accessor('memo', {
    header: '메모',
    size: 100,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (cell) => cell.getValue() || '-',
  }),
];

export default columns;
